import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { FileText, Shield, RotateCcw, Cookie, Info, Mail, LifeBuoy, ScrollText } from "lucide-react";

const DOCS = {
  terms: {
    icon: <FileText size={22} />,
    title: "Terms of Service",
    updated: "18 Feb 2026",
    body: [
      ["Acceptance", "By accessing or using To-Let Rentals (the \"Service\"), you agree to be bound by these Terms of Service. If you do not agree, please do not use the Service."],
      ["Eligibility", "You must be at least 18 years of age and legally competent to enter into contracts under Indian law to use the Service."],
      ["Your account", "You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. You agree to provide accurate, current and complete information during registration."],
      ["Listings by owners", "Owners are solely responsible for the accuracy, legality and lawful right to list any property. To-Let Rentals is a marketplace connecting owners and renters/buyers directly; we are not a party to any rental or sale transaction."],
      ["Prohibited use", "You may not: (a) post false, misleading or fraudulent listings, (b) circumvent brokerage/agency laws, (c) scrape, spam or misuse the Service, (d) upload unlawful content."],
      ["Fees and payments", "Property listings are free. Optional paid features such as Boost/promotion or paid contact packs are billed as displayed at checkout. Payments are processed by our payment partner; refunds follow our Refund Policy."],
      ["Content moderation", "We may review, approve, reject or remove any listing or content at our sole discretion, especially if it violates these Terms, applicable law or community standards."],
      ["Disclaimers", "The Service is provided on an \"as-is\" and \"as-available\" basis without warranties of any kind. We do not guarantee the accuracy of listings or the conduct of users."],
      ["Limitation of liability", "To the maximum extent permitted by law, To-Let Rentals shall not be liable for indirect, incidental or consequential damages arising from your use of the Service."],
      ["Governing law", "These Terms are governed by the laws of India. Courts in Bengaluru shall have exclusive jurisdiction, subject to applicable consumer-protection statutes."],
      ["Changes", "We may update these Terms from time to time. Continued use after changes constitutes acceptance of the revised Terms."],
    ],
  },
  privacy: {
    icon: <Shield size={22} />,
    title: "Privacy Policy",
    updated: "18 Feb 2026",
    body: [
      ["Data we collect", "When you register, we collect your name, email, phone number, city and state. When you list a property, we also store the details you provide (title, description, price, photos, address). We log basic access data such as timestamps and IP for security and abuse prevention."],
      ["How we use it", "We use your information to (a) provide and improve the Service, (b) show your listings and contact details to logged-in users who choose to reveal them, (c) prevent fraud, (d) send account-related notifications, and (e) analyse aggregate site usage."],
      ["Sharing", "We do not sell your personal data. Owner contact details are only revealed to logged-in users who complete a reveal or a paid unlock. We may share data with service providers (hosting, payments) under strict confidentiality."],
      ["Cookies and local storage", "We use essential cookies/local storage for authentication and preferences. See our Cookie Policy."],
      ["Retention", "We retain your account and listings for as long as your account is active. You can request deletion any time by writing to support."],
      ["Your rights", "You have the right to access, correct, or delete your personal data. Contact support@to-let-rentals.com to exercise these rights."],
      ["Children", "Our Service is not intended for users under 18. We do not knowingly collect data from minors."],
      ["Security", "We use industry-standard security including bcrypt password hashing, HTTPS/TLS, JWT tokens and server-side moderation. No system is 100% secure, so please use a strong unique password."],
      ["Contact", "Questions about this policy? Email us at the support address at the bottom of the page."],
    ],
  },
  refund: {
    icon: <RotateCcw size={22} />,
    title: "Refund & Cancellation Policy",
    updated: "18 Feb 2026",
    body: [
      ["Free listings", "Basic property listings are free. There are no charges to list or search on To-Let Rentals, so no refund applies."],
      ["Contact packs", "Prepaid contact-reveal packs are non-refundable once any credit has been used. Unused packs may be refunded within 7 days of purchase if you write to support with your order ID."],
      ["Boost / featured listings", "Boost promotions are non-refundable once the promotion period has begun. If the boost never activated due to a technical error on our side, we will re-run it or refund pro-rata."],
      ["How to request", "Email support@to-let-rentals.com with your registered account, order/transaction ID and a brief reason. Refund decisions are processed within 5-7 business days."],
      ["Chargebacks", "Please contact us before initiating a chargeback — we can usually resolve most concerns quickly and directly."],
    ],
  },
  cookies: {
    icon: <Cookie size={22} />,
    title: "Cookie Policy",
    updated: "18 Feb 2026",
    body: [
      ["What are cookies?", "Cookies and browser local storage are small files a site stores in your browser to remember information about you."],
      ["Essential cookies", "We use essential cookies for authentication (keeping you logged in), CSRF protection, and remembering preferences like your last-seen city or dismissed onboarding tips."],
      ["Analytics", "We may use privacy-respecting analytics to understand aggregate usage patterns. We do not build advertising profiles or sell cookie data."],
      ["Managing cookies", "You can clear cookies through your browser settings. Doing so will log you out and reset preferences, but you can continue to use most of the Service."],
    ],
  },
  about: {
    icon: <Info size={22} />,
    title: "About To-Let Rentals",
    updated: "18 Feb 2026",
    body: [
      ["Our mission", "Renting or buying a home in India shouldn't require a broker's cut, three phone calls, and a mystery viewing charge. To-Let Rentals connects renters and buyers directly with verified owners, so people spend their money on their next home — not on middlemen."],
      ["What we do", "We give every owner a free listing with photos and a video walkthrough, help renters find homes by neighbourhood or Discover-style scrolling, and use AI to estimate fair rent based on live comparable listings."],
      ["Zero brokerage promise", "We never charge or accept commission from any transaction between an owner and a renter/buyer. Optional paid boosts help owners get more visibility — that's the only way we make money."],
      ["Trust and safety", "Owners can verify themselves with government ID for a Verified badge. Every listing is manually reviewed before it goes live. Reported listings are taken down while investigated."],
      ["Get in touch", "Feedback, partnerships or press queries: write to us at the support email below."],
    ],
  },
  guidelines: {
    icon: <ScrollText size={22} />,
    title: "Community Guidelines",
    updated: "18 Feb 2026",
    body: [
      ["Be truthful", "Post real listings only. Photos must reflect the actual property. Rent and terms must be accurate. Misleading listings will be removed and repeat offenders banned."],
      ["No brokerage disguised as owners", "Brokers listing as owners will have their listings taken down. If you are a professional agent, contact us — we're working on a separate agent product."],
      ["Respect privacy", "Do not share phone numbers or personal details of others without consent. Use in-app reveal instead of scraping listings."],
      ["No discrimination", "Rejecting tenants based on caste, religion, marital status, gender, sexual orientation or state of origin violates our guidelines and Indian anti-discrimination principles."],
      ["Report abuse", "See something off? Email support with the listing URL. We investigate every report."],
    ],
  },
  contact: {
    icon: <LifeBuoy size={22} />,
    title: "Contact Us",
    updated: null,
    body: [],
  },
};

export default function LegalPage() {
  const { doc } = useParams();
  const info = DOCS[doc] || DOCS.terms;
  const [site, setSite] = useState({ support_email: "support@to-let-rentals.com", support_phone: "" });

  useEffect(() => {
    api.get("/site-info").then(({ data }) => setSite((s) => ({ ...s, ...data }))).catch(() => {});
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16" data-testid={`legal-${doc}`}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-[#FBF6EC] border border-[#EFE3D0] flex items-center justify-center text-[#7A1E2A]">
          {info.icon}
        </div>
        <div>
          {info.updated && <p className="label-eyebrow text-gray-500">Last updated · {info.updated}</p>}
          <h1 className="font-display text-4xl md:text-5xl text-[#7A1E2A]">{info.title}</h1>
        </div>
      </div>

      {doc === "contact" ? (
        <div className="bg-white border border-[#EFE3D0] rounded-2xl p-8 space-y-4">
          <p className="text-gray-700">
            We would love to hear from you. For questions, feedback, listing help, refund requests, or press queries, reach us at:
          </p>
          <div className="flex items-center gap-3 text-lg">
            <Mail size={18} className="text-[#7A1E2A]" />
            <a href={`mailto:${site.support_email}`} className="text-[#7A1E2A] font-semibold hover:underline">{site.support_email}</a>
          </div>
          {site.support_phone && (
            <div className="text-gray-700 pl-7 text-sm">Phone: {site.support_phone}</div>
          )}
          <p className="text-sm text-gray-500 pt-4 border-t border-[#EFE3D0]">
            Response time: 1-2 business days. Please include your registered email and, if applicable, a screenshot or order ID.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#EFE3D0] rounded-2xl p-8 md:p-10 space-y-6">
          {info.body.map(([heading, para]) => (
            <div key={heading}>
              <h2 className="font-display text-2xl text-[#1B0F14] mb-2">{heading}</h2>
              <p className="text-gray-700 leading-relaxed">{para}</p>
            </div>
          ))}
          <div className="pt-4 border-t border-[#EFE3D0] text-sm text-gray-500">
            Questions? Email <a href={`mailto:${site.support_email}`} className="text-[#7A1E2A] font-semibold hover:underline">{site.support_email}</a>.
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-8 text-sm">
        {Object.keys(DOCS).map((k) => (
          <Link
            key={k}
            to={`/legal/${k}`}
            data-testid={`legal-nav-${k}`}
            className={`px-4 py-1.5 rounded-full border transition-colors ${
              k === doc
                ? "bg-[#7A1E2A] text-white border-[#7A1E2A]"
                : "bg-white text-gray-700 border-[#EFE3D0] hover:border-[#7A1E2A] hover:text-[#7A1E2A]"
            }`}
          >
            {DOCS[k].title}
          </Link>
        ))}
      </div>
    </div>
  );
}
